/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { styled, SxProps, Theme, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { useIsFirstLevelRoute } from "./layout/hooks/useIsFirstLevelRoute";
import { Button, ButtonProps } from "./Button";
import MaterialSymbol from "./MaterialSymbol";

interface Props extends ButtonProps {
  text: string;
  onClick?: () => void;
  hideOnMobile?: boolean;
}

const StyledButton = styled(Button)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  cursor: "pointer",
  ...theme.typography.body2,
  color: theme.colors.primary500,
  padding: "2px 8px",
  marginLeft: "-8px",
  marginBottom: "16px",
  "& span": {
    fontWeight: 300,
    transform: "rotate(-180deg)",
    marginRight: "8px",
  },
  "&:hover": {
    background: theme.colors.primary100,
  },
}));

export const BackButton = ({ text, onClick, hideOnMobile, sx, ...rest }: Props) => {
  const navigate = useNavigate();
  const isFirstLevelRoute = useIsFirstLevelRoute();
  const goBack = () => {
    navigate(-1);
  };

  // Defaults to hidden on second-level+ routes, where the mobile header already renders its own back chevron.
  const shouldHideOnMobile = hideOnMobile ?? !isFirstLevelRoute;
  const hideOnMobileSx: SxProps<Theme> = { display: { xs: "none", md: "inline-flex" } };

  return (
    <StyledButton
      onClick={onClick ?? goBack}
      sx={[...(shouldHideOnMobile ? [hideOnMobileSx] : []), ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    >
      <MaterialSymbol>arrow_right_alt</MaterialSymbol>
      <Typography variant="body2" color="inherit">
        {text}
      </Typography>
    </StyledButton>
  );
};
